import React, { useState, useEffect } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import CloseIcon24 from "../../../../assets/svg/icons/common/CloseIcon24";
import ArrowDown from "../../../../assets/svg/icons/common/ArrowDown";
import TagInput from "../../../../components/form/tag-input";
import Select from "../../../../components/form/select";
import Checkbox from "../../../../components/Checkbox/Index";
import Button from "../../../../components/new-button";
import { fetchResidualFilterData } from "../../../../services/residuals";
import { SearchOrganizations } from "../../../../../api";
import { isEntitySuperAdmin } from "../../../../../components/helper/helper";
import useResidualStore from "../../../../stores/residuals";
import useCommonStore from "../../../../stores/common";
import useUserStore from "../../../../stores/user";
import { useSavedFilters } from "../../../../hooks/useSavedFilters";
import SavedFiltersSection from "../../../../components/SavedFiltersSection/SavedFiltersSection";
import { isResidualFilterSnapshotEmpty } from "../../../../utils/savedFilterSnapshotEmpty";
import styles from "./style.module.scss";

const SAVED_FILTER_PAGE = "residuals";

function buildResidualSelectedFilters(snapshot) {
  const out = [];
  if (snapshot.orgFilter) {
    out.push(`orgFilter:${snapshot.orgFilter}`);
  }
  (snapshot.offices || []).forEach((v) => {
    out.push(`offices:${v}`);
  });
  (snapshot.branches || []).forEach((v) => {
    out.push(`branches:${v}`);
  });
  (snapshot.repCodes || []).forEach((v) => {
    out.push(`repCodes:${v}`);
  });
  return out;
}

const FilterDrawer = () => {
  const isMobile = useMediaQuery(768);
  const isSmallMobile = useMediaQuery(420);
  const filters = useResidualStore((state) => state.filters);
  const setFilters = useResidualStore((state) => state.setFilters);
  const setFilterApplied = useResidualStore((state) => state.setFilterApplied);
  const filterDrawerOpen = useCommonStore((state) => state.filterDrawerOpen);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const user = useUserStore((state) => state.user);
  const [localFilters, setLocalFilters] = useState(filters);
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (filterDrawerOpen) {
      setLocalFilters(filters);
    }
  }, [filterDrawerOpen, filters]);

  const handleFilterChange = (field, value) => {
    setLocalFilters({
      ...localFilters,
      [field]: value,
      selectedFilters: updateSelectedFilters(field, value),
    });
  };

  const updateSelectedFilters = (field, value) => {
    const currentSelected = [...localFilters.selectedFilters];

    // Remove existing filters for this field
    const filtered = currentSelected.filter(
      (filter) => !filter.startsWith(`${field}:`),
    );

    // Add new filters
    if (Array.isArray(value)) {
      return [...filtered, ...value.map((v) => `${field}:${v}`)];
    } else if (value && value !== "") {
      // Handle single value filters like orgFilter
      return [...filtered, `${field}:${value}`];
    }
    return filtered;
  };

  const handleRemoveFilter = (filter) => {
    const [field, value] = filter.split(":");
    if (field === "orgFilter") {
      handleFilterChange("orgFilter", "");
    } else {
      const newValues = localFilters[field].filter((v) => v !== value);
      handleFilterChange(field, newValues);
    }
  };

  const handleClear = () => {
    const clearedFilters = {
      ...filters,
      cards: [],
      offices: [],
      branches: [],
      repCodes: [],
      orgFilter: "",
      selectedFilters: [],
    };
    setLocalFilters(clearedFilters);
    setFilters(clearedFilters);
  };

  const [filterOptions, setFilterOptions] = useState({
    offices: [],
    branches: [],
    repCodes: [],
  });

  const [showMore, setShowMore] = useState({
    offices: false,
    branches: false,
    repCodes: false,
  });

  const INITIAL_SHOW_COUNT = 5;

  const getVisibleOptions = (options, type) => {
    if (showMore[type]) return options;
    return options.slice(0, INITIAL_SHOW_COUNT);
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const data = await fetchResidualFilterData();
        setFilterOptions({
          offices: data.office?.map((o) => ({ value: o, label: o })) || [],
          branches: data.branch?.map((b) => ({ value: b, label: b })) || [],
          repCodes: data.rep_code?.map((r) => ({ value: r, label: r })) || [],
        });
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchFilterOptions();

    // Fetch organizations on mount
    SearchOrganizations(null).then((res) => {
      if (!res.success) {
        console.error("There was a problem fetching organizations");
        return;
      }
      const orgsOptions = res?.data?.map((org) => ({
        label: org.name,
        value: org.id,
      }));
      setOrganizations(orgsOptions);
    });
  }, []);

  const handleOnSearchInputChange = async (term) => {
    try {
      const res = await SearchOrganizations({ term });
      if (!res.success) {
        console.error("There was a problem fetching organizations");
        return;
      }

      const orgsOptions = res?.data.map((org) => ({
        label: org.name,
        value: org.id,
      }));

      setOrganizations(orgsOptions);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const handleOnOrganizationSelectChange = (selectedOption) => {
    const value =
      typeof selectedOption === "object"
        ? selectedOption.value
        : selectedOption;
    handleFilterChange("orgFilter", value);
  };

  const getCurrentFilterSnapshot = () => ({
    orgFilter: localFilters.orgFilter || "",
    offices: [...(localFilters.offices || [])],
    branches: [...(localFilters.branches || [])],
    repCodes: [...(localFilters.repCodes || [])],
  });

  const applySavedFilterSnapshot = (saved) => {
    const orgFilter = saved.orgFilter || "";
    const offices = saved.offices || [];
    const branches = saved.branches || [];
    const repCodes = saved.repCodes || [];
    setLocalFilters((prev) => ({
      ...prev,
      orgFilter,
      offices,
      branches,
      repCodes,
      selectedFilters: buildResidualSelectedFilters({
        orgFilter,
        offices,
        branches,
        repCodes,
      }),
    }));
  };

  const getAppliedSnapshot = () => {
    const f = useResidualStore.getState().filters;
    return {
      orgFilter: f.orgFilter || "",
      offices: [...(f.offices || [])],
      branches: [...(f.branches || [])],
      repCodes: [...(f.repCodes || [])],
    };
  };

  const savedFiltersApi = useSavedFilters({
    page: SAVED_FILTER_PAGE,
    drawerOpen: filterDrawerOpen,
    getFilterSnapshot: getCurrentFilterSnapshot,
    applyFilterSnapshot: applySavedFilterSnapshot,
    isSnapshotEmpty: isResidualFilterSnapshotEmpty,
    getAppliedSnapshot,
  });

  const handleApply = () => {
    setFilters(localFilters);
    setFilterApplied(true);
    savedFiltersApi.commitAppliedSavedFilter();
    setFilterDrawerOpen(false);
  };

  const getActiveFilterChips = () => {
    const chips = [];
    if (localFilters.orgFilter) {
      const org = organizations.find((o) => o.value === localFilters.orgFilter);
      chips.push({
        key: "org",
        label: org?.label ?? localFilters.orgFilter,
        onRemove: () => handleFilterChange("orgFilter", ""),
      });
    }
    localFilters.offices.forEach((value) => {
      chips.push({
        key: `office-${value}`,
        label: value,
        onRemove: () =>
          handleFilterChange(
            "offices",
            localFilters.offices.filter((v) => v !== value)
          ),
      });
    });
    localFilters.branches.forEach((value) => {
      chips.push({
        key: `branch-${value}`,
        label: value,
        onRemove: () =>
          handleFilterChange(
            "branches",
            localFilters.branches.filter((v) => v !== value)
          ),
      });
    });
    localFilters.repCodes.forEach((value) => {
      chips.push({
        key: `repCode-${value}`,
        label: value,
        onRemove: () =>
          handleFilterChange(
            "repCodes",
            localFilters.repCodes.filter((v) => v !== value)
          ),
      });
    });
    return chips;
  };

  const activeChips = getActiveFilterChips();

  return (
    <Drawer
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      direction={isMobile ? "left" : "right"}
      size={isSmallMobile ? "320px" : isMobile ? "375px" : "440px"}
      zIndex={8888}
      overlayColor={"#000"}
      overlayOpacity={0.5}
    >
      <div className={styles.filterDrawerContainer}>
        <div className={styles.drawer_title_block}>
          <div className={styles.filterDrawerHeader}>
            <ButtonIcon onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon24 />
            </ButtonIcon>
          </div>

          <div className={styles.filterDrawerTitle}>
            <h5>Filter</h5>
            {activeChips.length > 0 && (
              <div className={styles.filterChipsArea}>
                {activeChips.map((chip) => (
                  <div key={chip.key} className={styles.filterChip}>
                    {chip.label}
                    <button
                      type="button"
                      onClick={chip.onRemove}
                      className={styles.filterChipClose}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.filterContentContainer}>
          <SavedFiltersSection
            savedFilterOptions={savedFiltersApi.savedFilterOptions}
            selectedSavedFilter={savedFiltersApi.selectedSavedFilter}
            onSavedFilterChange={savedFiltersApi.handleSavedFilterSelect}
            onDeleteSavedFilter={savedFiltersApi.handleDeleteSavedFilter}
            isDeletingFilter={savedFiltersApi.isDeletingFilter}
            editFilterName={savedFiltersApi.editFilterName}
            onEditFilterNameChange={savedFiltersApi.setEditFilterName}
            onUpdateSavedFilter={savedFiltersApi.handleUpdateSavedFilter}
            isUpdatingFilter={savedFiltersApi.isUpdatingFilter}
            showSaveFilterInput={savedFiltersApi.showSaveFilterInput}
            onShowSaveInput={savedFiltersApi.openSaveFilterInput}
            newFilterName={savedFiltersApi.newFilterName}
            onNewFilterNameChange={savedFiltersApi.setNewFilterName}
            onSaveFilter={savedFiltersApi.handleSaveFilter}
            isSavingFilter={savedFiltersApi.isSavingFilter}
            onCancelNewFilter={() => {
              savedFiltersApi.cancelSaveFilterInput();
              savedFiltersApi.setNewFilterName("");
            }}
          />

          {isEntitySuperAdmin(user) && (
            <div className={styles.statusFilter}>
              <h5 className={styles.heading}>ORGANIZATION</h5>
              <div className={styles.filterContainer}>
                <Select
                  async
                  search
                  options={organizations}
                  value={
                    localFilters.orgFilter
                      ? organizations.find(
                          (o) => o.value === localFilters.orgFilter
                        ) ?? localFilters.orgFilter
                      : null
                  }
                  onSearchChange={handleOnSearchInputChange}
                  onChange={handleOnOrganizationSelectChange}
                  placeholder="Search organizations"
                />
              </div>
            </div>
          )}

          <div className={styles.statusFilter}>
            <h5 className={styles.heading}>OFFICES</h5>
            <div className={styles.filterContainer}>
              <div className={styles.tag_input_group}>
                <TagInput
                  id="offices"
                  name="offices"
                  options={filterOptions.offices}
                  value={filterOptions.offices.filter((opt) =>
                    localFilters.offices.includes(opt.value)
                  )}
                  onChange={(value) => {
                    const newValues = value.map((option) => option.value);
                    handleFilterChange("offices", newValues);
                  }}
                  placeholder="Select offices"
                />
              </div>
            </div>
          </div>

          <div className={styles.statusFilter}>
            <h5 className={styles.heading}>BRANCHES</h5>
            <div className={styles.filterContainer}>
              <div className={styles.tag_input_group}>
                <TagInput
                  id="branches"
                  name="branches"
                  options={filterOptions.branches}
                  value={filterOptions.branches.filter((opt) =>
                    localFilters.branches.includes(opt.value)
                  )}
                  onChange={(value) => {
                    const newValues = value.map((option) => option.value);
                    handleFilterChange("branches", newValues);
                  }}
                  placeholder="Select branches"
                />
              </div>
            </div>
          </div>

          <div className={styles.statusFilter}>
            <h5 className={styles.heading}>REP CODES</h5>
            <div className={styles.filterContainer}>
              <div className={styles.tag_input_group}>
                <TagInput
                  id="repCodes"
                  name="repCodes"
                  options={filterOptions.repCodes}
                  value={filterOptions.repCodes.filter((opt) =>
                    localFilters.repCodes.includes(opt.value)
                  )}
                  onChange={(value) => {
                    const newValues = value.map((option) => option.value);
                    handleFilterChange("repCodes", newValues);
                  }}
                  placeholder="Select rep codes"
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" size="large" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="primary" size="large" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default FilterDrawer;